import { z } from "zod";

export const DISCOVERY_API = "/api/discovery-workspace";
export const OWNING_ROLES = ["Content Manager", "Staff Interface", "Student Interface"] as const;
export const SESSION_KINDS = ["interview", "workshop"] as const;
export const SESSION_STATUSES = ["scheduled", "completed", "cancelled"] as const;
export const PRIORITIES = ["low", "medium", "high"] as const;

const titleSchema = z.string().trim().min(1, "Enter a title.").max(200, "Use no more than 200 characters.");

/** datetime-local represents the administrator's local time, never UTC. */
export function localScheduleToUtc(value: string): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) throw new Error("Enter a valid local date and time.");
  const [, year, month, day, hour, minute, second = "0"] = match;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())
    || date.getFullYear() !== Number(year) || date.getMonth() + 1 !== Number(month)
    || date.getDate() !== Number(day) || date.getHours() !== Number(hour)
    || date.getMinutes() !== Number(minute) || date.getSeconds() !== Number(second)) {
    throw new Error("Enter a valid local date and time.");
  }
  return date.toISOString();
}

export function utcScheduleToLocal(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(date.getFullYear()).padStart(4, "0")}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export const sessionFormSchema = z.object({
  title: titleSchema,
  kind: z.enum(SESSION_KINDS),
  scheduledAt: z.string().refine(value => {
    try { localScheduleToUtc(value); return true; } catch { return false; }
  }, "Enter a valid local date and time."),
  notes: z.string().max(10000, "Use no more than 10,000 characters."),
  status: z.enum(SESSION_STATUSES),
});

export const useCaseFormSchema = z.object({
  title: titleSchema,
  description: z.string().max(10000, "Use no more than 10,000 characters."),
  owningRole: z.enum(OWNING_ROLES, { required_error: "Choose exactly one owning role." }),
  priority: z.enum(PRIORITIES),
});

export type SessionFormValues = z.infer<typeof sessionFormSchema>;
export type UseCaseValues = z.infer<typeof useCaseFormSchema>;
export type SessionValues = Omit<SessionFormValues, "scheduledAt"> & { scheduledAt: string | null };
export type DiscoverySession = SessionValues & { id: string };
export type DiscoveryUseCase = UseCaseValues & { id: string };
export type DiscoveryWorkspace = { sessions: DiscoverySession[]; useCases: DiscoveryUseCase[] };

export function canAccessDiscovery(role: string | null | undefined): boolean {
  return role === "super_admin" || role === "org_admin";
}

export function discoveryQueryKey(organizationId: string, userId: string) {
  return [DISCOVERY_API, organizationId, userId] as const;
}

export function discoveryErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The discovery workspace could not be loaded. Please try again.";
}

export function isDiscoverySchemaPending(error: unknown): boolean {
  // Supports structured errors if the shared API layer preserves them.
  // Current apiRequest reduces errors to a friendly message, so the generic
  // unavailable state also explains the migration gate and disables writes.
  if (!error || typeof error !== "object") return false;
  const structured = error as { code?: string; message?: string };
  return structured.code === "DISCOVERY_SCHEMA_PENDING"
    || /DISCOVERY_SCHEMA_PENDING|schema.{0,40}pending|migration.{0,40}approval|awaiting.{0,40}migration/i.test(structured.message ?? "");
}

export function formatSchedule(value: string | null): string {
  if (!value) return "Not yet scheduled";
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "Invalid schedule";
}
